import React from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, ReferenceLine, ReferenceArea, Tooltip,
} from 'recharts';
import LeagueRangesTable from './LeagueRangesTable';

const WAIT_STATES = ['no bet','no under','no over','jugable','wait','esperar','salir','sin línea','sin linea'];
function isWaitDecision(d) { return WAIT_STATES.some(w => d.includes(w)); }
function getVerdictClass(dec) {
  const d = (dec||'').toLowerCase();
  if (isWaitDecision(d)) return 'wait';
  if (d.includes('early')||d.includes('temprano')) return 'early';
  if (d.includes('late')) return 'late';
  if (d.includes('fuerte')) return d.includes('under')?'fuerte-under':'fuerte-over';
  if (d.includes('under')) return 'under';
  if (d.includes('over'))  return 'over';
  return 'wait';
}
function getVerdictLabel(dec) {
  const d = (dec||'').toLowerCase();
  if (isWaitDecision(d)) return d.includes('sin línea')||d.includes('sin linea')?'SIN LÍNEA':'ESPERAR';
  if (d.includes('early')||d.includes('temprano')) return 'TEMPRANO';
  if (d.includes('late')) return 'TARDÍO';
  if (d.includes('fuerte')) return d.includes('under')?'UNDER ★':'OVER ★';
  if (d.includes('under')||d.includes('over')) return 'VIGILAR';
  return 'ESPERAR';
}
function fmt(val, suffix='') {
  if (val===undefined||val===null||val===''||val==='-'||val===0) return '—';
  return val+suffix;
}
function parseStake(stake) {
  const m = String(stake||'').match(/-?\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : 0;
}

// ── KV row ────────────────────────────────────────────────────
function KvRow({ label, children }) {
  return (
    <div className="kv-row">
      <span className="kv-k">{label}</span>
      {children}
    </div>
  );
}

// ── Stat bar (tiros de campo, triples) ────────────────────────
// value === null/undefined → muestra "—" y barra vacía (API sin cobertura).
// value === 0 → muestra "0%" con barra vacía (dato real, partido temprano).
function StatBar({ label, value }) {
  const isNull = value === null || value === undefined;
  const numVal = isNull ? 0 : Number(value);
  const safeWidth = isNaN(numVal) ? 0 : Math.max(0, Math.min(100, numVal));
  const display = isNull || isNaN(numVal) ? '—' : `${numVal}%`;
  return (
    <div className="kv-row statbar">
      <span className="kv-k">{label}</span>
      <span className="sb-wrap">
        <span className="sb-bar"><i style={{ width: `${safeWidth}%` }} /></span>
        <span className="kv-v">{display}</span>
      </span>
    </div>
  );
}

// ── Tag inline (tipo partido, ritmo ajustado, impacto) ────────
function Tag({ tone, children }) {
  // tone: 'low' | 'info' | 'go' | 'warn'
  return <span className={`kv-v tag tag-${tone}`}>{children}</span>;
}

// ── Callout ───────────────────────────────────────────────────
function Callout({ ok, children }) {
  return (
    <div className={`callout ${ok ? 'ok' : 'warn'}`}>
      {ok ? (
        <svg viewBox="0 0 24 24" className="i">
          <path d="M4 12l5 5L20 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" className="i">
          <path d="M12 8v4M12 16h.01M10.3 3.7L2 20h20L13.7 3.7a2 2 0 0 0-3.4 0z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {children}
    </div>
  );
}

// ── TEAM PACE ANALYSIS (NUEVO) ────────────────────────────────
// Muestra el pace por equipo + liga + combinado. Responsive mobile + desktop.
function TeamPaceAnalysis({ pick }) {
  const {
    home_team,
    away_team,
    liga_pace,
    team_pace_home,
    team_pace_home_games,
    team_pace_away,
    team_pace_away_games,
    combined_pace,
    pace,
  } = pick;

  const hasHome = team_pace_home != null && team_pace_home > 0;
  const hasAway = team_pace_away != null && team_pace_away > 0;
  const hasTeamData = hasHome || hasAway;

  // Si no hay ningun dato util, no renderizar nada
  if (!hasTeamData && (!liga_pace || liga_pace <= 0)) return null;

  const combinedShow = (combined_pace != null && combined_pace > 0) ? combined_pace : pace;

  return (
    <div className="tp-analysis">
      <div className="tp-title">ANÁLISIS DE PACE</div>

      <div className="tp-rows">
        {hasHome && (
          <div className="tp-row">
            <div className="tp-left">
              <span className="tp-dot" style={{ background: '#60a5fa' }} />
              <span className="tp-label" title={home_team}>
                {home_team || 'Local'}
                <span className="tp-games"> · {team_pace_home_games} pj</span>
              </span>
            </div>
            <span className="tp-value">{Number(team_pace_home).toFixed(1)}</span>
          </div>
        )}

        {hasAway && (
          <div className="tp-row">
            <div className="tp-left">
              <span className="tp-dot" style={{ background: '#f87171' }} />
              <span className="tp-label" title={away_team}>
                {away_team || 'Visitante'}
                <span className="tp-games"> · {team_pace_away_games} pj</span>
              </span>
            </div>
            <span className="tp-value">{Number(team_pace_away).toFixed(1)}</span>
          </div>
        )}

        {liga_pace != null && liga_pace > 0 && (
          <div className="tp-row tp-league">
            <div className="tp-left">
              <span className="tp-dot" style={{ background: 'var(--muted-2)' }} />
              <span className="tp-label">Liga</span>
            </div>
            <span className="tp-value tp-muted">{Number(liga_pace).toFixed(1)}</span>
          </div>
        )}

        {hasTeamData && combinedShow && (
          <div className="tp-combined">
            <div className="tp-combined-label">
              <b>PACE COMBINADO</b>
              <span className="tp-combined-sub">usado por el motor</span>
            </div>
            <span className="tp-combined-value">{Number(combinedShow).toFixed(1)}</span>
          </div>
        )}

        {!hasTeamData && liga_pace > 0 && (
          <div className="tp-no-team">
            Equipos sin datos previos — usando pace de liga
          </div>
        )}
      </div>

      {/* Estilos inline - responsive mobile + desktop */}
      <style>{`
        .tp-analysis {
          background: transparent;
          border: 1px solid var(--line);
          border-radius: 10px;
          padding: 10px 12px;
          margin-top: 10px;
        }
        .tp-title {
          font-size: 10px;
          font-weight: 700;
          color: var(--muted);
          letter-spacing: 0.08em;
          margin-bottom: 8px;
          padding-bottom: 6px;
          border-bottom: 1px solid var(--line);
        }
        .tp-rows {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .tp-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          min-height: 20px;
        }
        .tp-left {
          display: flex;
          align-items: center;
          gap: 7px;
          flex: 1;
          min-width: 0;
          overflow: hidden;
        }
        .tp-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .tp-label {
          color: var(--ink);
          font-size: 12px;
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
          min-width: 0;
        }
        .tp-games {
          color: var(--muted);
          font-size: 10px;
          font-weight: 400;
        }
        .tp-value {
          font-size: 13px;
          font-weight: 700;
          font-variant-numeric: tabular-nums;
          color: var(--ink);
          flex-shrink: 0;
        }
        .tp-value.tp-muted {
          color: var(--muted);
          font-weight: 600;
        }
        .tp-league {
          padding-top: 2px;
        }
        .tp-combined {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
          margin-top: 6px;
          padding: 7px 9px;
          background: var(--pos-soft);
          border: 1px solid color-mix(in oklab, var(--pos) 30%, transparent);
          border-radius: 6px;
        }
        .tp-combined-label {
          display: flex;
          flex-direction: column;
          gap: 1px;
          min-width: 0;
          flex: 1;
        }
        .tp-combined-label b {
          color: var(--pos);
          font-size: 10px;
          letter-spacing: 0.05em;
          font-weight: 700;
        }
        .tp-combined-sub {
          color: var(--muted);
          font-size: 9.5px;
          font-weight: 400;
        }
        .tp-combined-value {
          color: var(--pos);
          font-size: 15px;
          font-weight: 800;
          font-variant-numeric: tabular-nums;
          flex-shrink: 0;
        }
        .tp-no-team {
          color: var(--muted);
          font-size: 11px;
          font-style: italic;
          padding: 4px;
          text-align: center;
        }

        /* Mobile - ajustes en viewport chicos */
        @media (max-width: 420px) {
          .tp-analysis {
            padding: 9px 10px;
          }
          .tp-label { font-size: 11.5px; }
          .tp-value { font-size: 12.5px; }
          .tp-combined-value { font-size: 14px; }
          .tp-combined-label b { font-size: 9.5px; }
        }
      `}</style>
    </div>
  );
}

// ── STATS COMPARISON (rediseño 2026-05-23) ───────────────────
// Reemplaza la tabla "Stats clave" por una comparativa visual estilo
// box-score: anillos de progreso para los % (tiro campo / triples / libres)
// y barras a dos lados (local vs visitante) para los conteos (pérdidas, TL int).
// Modo claro. Colores: local=azul, visitante=naranja (distinguibles en blanco).
const SC_HOME = '#3b82f6';   // local
const SC_AWAY = '#f97316';   // visitante

function StatRing({ value, color, size = 50 }) {
  const stroke = 5;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const isNull = value === null || value === undefined || isNaN(Number(value));
  const pct = isNull ? 0 : Math.max(0, Math.min(100, Number(value)));
  const offset = circ * (1 - pct / 100);
  const display = isNull ? '—' : `${Math.round(pct)}%`;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="sc-ring">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--sc-track)" strokeWidth={stroke} />
      {!isNull && (
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      )}
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="sc-ring-pct">{display}</text>
    </svg>
  );
}

// made/attempts → "9/10" (o null si falta data → no se muestra).
function maStr(m, a) {
  if (m === null || m === undefined || a === null || a === undefined) return null;
  return `${m}/${a}`;
}

function RingRow({ label, homePct, awayPct, homeMA, awayMA, avgPct }) {
  return (
    <div className="sc-ring-row">
      <span className="sc-ma">{homeMA || ''}</span>
      <StatRing value={homePct} color={SC_HOME} />
      <div className="sc-ring-mid">
        <div className="sc-ring-label">{label}</div>
        {avgPct !== null && avgPct !== undefined && (
          <div className="sc-ring-comb">prom. {avgPct}%</div>
        )}
      </div>
      <StatRing value={awayPct} color={SC_AWAY} />
      <span className="sc-ma sc-ma-r">{awayMA || ''}</span>
    </div>
  );
}

function VersusBar({ label, home, away }) {
  const hNull = home === null || home === undefined;
  const aNull = away === null || away === undefined;
  const h = hNull ? 0 : Number(home);
  const a = aNull ? 0 : Number(away);
  const sum = h + a;
  const empty = hNull && aNull;            // sin dato (API sin box en esa liga)
  const hPct = sum > 0 ? (h / sum) * 100 : 50;
  const aPct = 100 - hPct;
  return (
    <div className="sc-bar-row">
      <div className="sc-bar-head">
        <span className={`sc-bar-num ${!hNull && h >= a ? 'lead' : ''}`} style={{ color: !hNull && h >= a ? SC_HOME : undefined }}>{hNull ? '—' : home}</span>
        <span className="sc-bar-label">{label}</span>
        <span className={`sc-bar-num ${!aNull && a > h ? 'lead' : ''}`} style={{ color: !aNull && a > h ? SC_AWAY : undefined }}>{aNull ? '—' : away}</span>
      </div>
      <div className="sc-bar-track">
        {!empty && <span className="sc-bar-fill" style={{ width: `${hPct}%`, background: SC_HOME }} />}
        {!empty && <span className="sc-bar-fill" style={{ width: `${aPct}%`, background: SC_AWAY }} />}
      </div>
    </div>
  );
}

function StatsComparison({ pick }) {
  const home = pick?.home_team || 'Local';
  const away = pick?.away_team || 'Visitante';

  // 2P: la API llama "field_goals" a los dobles → fg_pct actual ES 2P.
  const fg2pH = pick?.fg2_pct_home ?? pick?.fg_pct_home;
  const fg2pA = pick?.fg2_pct_away ?? pick?.fg_pct_away;
  const fg2avg = pick?.fg2_pct ?? pick?.fg_pct;

  // Tiros de campo REAL = 2P + 3P (calculado de makes/attempts si están).
  const fgmH = (pick?.fg2m_home != null && pick?.fg3m_home != null) ? pick.fg2m_home + pick.fg3m_home : null;
  const fgaH = (pick?.fg2a_home != null && pick?.fg3a_home != null) ? pick.fg2a_home + pick.fg3a_home : null;
  const fgmA = (pick?.fg2m_away != null && pick?.fg3m_away != null) ? pick.fg2m_away + pick.fg3m_away : null;
  const fgaA = (pick?.fg2a_away != null && pick?.fg3a_away != null) ? pick.fg2a_away + pick.fg3a_away : null;
  const pctFrom = (m, a) => (m == null || a == null || a === 0) ? null : Math.round((m / a) * 100);
  const fgPctH = pctFrom(fgmH, fgaH);
  const fgPctA = pctFrom(fgmA, fgaA);
  const fgAvg = (fgPctH != null && fgPctA != null) ? Math.round((fgPctH + fgPctA) / 2) : null;

  return (
    <div className="sc">
      <div className="sc-teams">
        <span className="sc-team"><i style={{ background: SC_HOME }} />{home}</span>
        <span className="sc-team">{away}<i style={{ background: SC_AWAY }} /></span>
      </div>

      <div className="sc-rings">
        <RingRow label="Tiros libres"
          homePct={pick?.ft_pct_home} awayPct={pick?.ft_pct_away} avgPct={pick?.ft_pct}
          homeMA={maStr(pick?.ftm_home, pick?.fta_home)} awayMA={maStr(pick?.ftm_away, pick?.fta_away)} />
        <RingRow label="Canastas de 2 puntos"
          homePct={fg2pH} awayPct={fg2pA} avgPct={fg2avg}
          homeMA={maStr(pick?.fg2m_home, pick?.fg2a_home)} awayMA={maStr(pick?.fg2m_away, pick?.fg2a_away)} />
        <RingRow label="Canastas de 3 puntos"
          homePct={pick?.fg3_pct_home} awayPct={pick?.fg3_pct_away} avgPct={pick?.fg3_pct}
          homeMA={maStr(pick?.fg3m_home, pick?.fg3a_home)} awayMA={maStr(pick?.fg3m_away, pick?.fg3a_away)} />
        <RingRow label="Tiros de campo"
          homePct={fgPctH} awayPct={fgPctA} avgPct={fgAvg}
          homeMA={maStr(fgmH, fgaH)} awayMA={maStr(fgmA, fgaA)} />
      </div>

      <div className="sc-divider" />

      <div className="sc-bars">
        <VersusBar label="Pérdidas de balón" home={pick?.turnovers_home} away={pick?.turnovers_away} />
        <VersusBar label="Robos"             home={pick?.steals_home}    away={pick?.steals_away} />
      </div>

      <style>{`
        .sc { --sc-track: var(--bg-subtle); padding-top: 2px; }
        .sc-teams {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 10px; font-size: 11.5px; font-weight: 700; color: var(--ink);
        }
        .sc-team { display: inline-flex; align-items: center; gap: 6px; max-width: 46%;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .sc-team i { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .sc-rings { display: flex; flex-direction: column; gap: 4px; }
        .sc-ring-row {
          display: grid; grid-template-columns: 38px 50px 1fr 50px 38px;
          align-items: center; gap: 8px; padding: 6px 0;
        }
        .sc-ma { font-size: 11px; font-weight: 600; color: var(--muted);
          font-variant-numeric: tabular-nums; white-space: nowrap; }
        .sc-ma-r { text-align: right; }
        .sc-ring { display: block; }
        .sc-ring-pct { font-size: 13px; font-weight: 800; fill: var(--ink);
          font-variant-numeric: tabular-nums; }
        .sc-ring-mid { text-align: center; min-width: 0; }
        .sc-ring-label { font-size: 12.5px; font-weight: 600; color: var(--ink);
          line-height: 1.2; }
        .sc-ring-comb { font-size: 10.5px; font-weight: 600; color: var(--muted);
          margin-top: 2px; font-variant-numeric: tabular-nums; }
        .sc-divider { height: 1px; background: var(--line); margin: 12px 0; }
        .sc-bars { display: flex; flex-direction: column; gap: 12px; }
        .sc-bar-row { display: flex; flex-direction: column; gap: 6px; }
        .sc-bar-head {
          display: grid; grid-template-columns: auto 1fr auto; align-items: center;
          gap: 10px; font-size: 12.5px;
        }
        .sc-bar-num { font-size: 14px; font-weight: 700; color: var(--ink);
          font-variant-numeric: tabular-nums; min-width: 18px; }
        .sc-bar-num:last-child { text-align: right; }
        .sc-bar-num.lead { font-weight: 800; }
        .sc-bar-label { text-align: center; font-size: 12px; font-weight: 600;
          color: var(--muted); }
        .sc-bar-track {
          display: flex; gap: 3px; height: 8px; border-radius: 999px; overflow: hidden;
        }
        .sc-bar-fill { height: 100%; border-radius: 999px; transition: width .3s ease; min-width: 2px; }

        @media (max-width: 420px) {
          .sc-ring-row { grid-template-columns: 30px 42px 1fr 42px 30px; gap: 6px; }
          .sc-ring-label { font-size: 11.5px; }
          .sc-ma { font-size: 10px; }
        }
      `}</style>
    </div>
  );
}

// ── EDGE CHART (evolución del edge en el partido) ─────────────
// Área verde = OVER (edge>0), azul = UNDER (edge<0). Líneas punteadas en
// ±15 (umbral señal fuerte). Divisores de cuarto + marcador del momento
// actual. Requiere pick.edge_history = [{ t: minuto_juego, e: edge }, ...].
// Si no hay serie (prod hasta que el bot la registre) → no renderiza nada.
// Minuto absoluto de juego → "Q2 · min 3" (minuto transcurrido dentro del
// cuarto). qLen = minutos por cuarto (10 o 12 según liga).
function fmtQuarterClock(t, qLen, handball = false) {
  // Handball: 2 mitades de 30' → "1T · min 12" / "2T · min 42" (min absoluto).
  if (handball) return t <= 30 ? `1T · min ${Math.round(t)}` : `2T · min ${Math.round(t)}`;
  if (!qLen || qLen <= 0) return `Min ${t}`;
  const qFloor = Math.floor(t / qLen);
  const qNum = qFloor + 1;
  const inQ = Math.round(t - qFloor * qLen);
  const label = qNum <= 4 ? `Q${qNum}` : `OT${qNum - 4}`;
  return `${label} · min ${inQ}`;
}

function EdgeTooltip({ active, payload, qLen, handball }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const e = p.e;
  return (
    <div style={{
      background: 'var(--bg-elev, #fff)', border: '1px solid var(--line)',
      borderRadius: 8, padding: '6px 9px', fontSize: 11, fontWeight: 600,
      boxShadow: '0 4px 14px rgba(0,0,0,.10)',
    }}>
      <div style={{ color: 'var(--muted)' }}>{fmtQuarterClock(p.t, qLen, handball)}</div>
      <div style={{ color: e >= 0 ? 'var(--pos)' : 'var(--info)' }}>
        {e >= 0 ? 'OVER ' : 'UNDER '}{e >= 0 ? '+' : ''}{e.toFixed(1)}
      </div>
      {typeof p.l === 'number' && (
        <div style={{ color: 'var(--warn)' }}>Línea {p.l}</div>
      )}
    </div>
  );
}

// Duración total del partido por liga (NBA/G-League = 48 / cuartos de 12;
// WNBA + FIBA/internacional = 40 / cuartos de 10). El bot hoy NO manda
// `duracion` en el pick, así que la derivamos del nombre de liga. Ojo WNBA:
// "NBA W" contiene "nba" pero juega 10-min → se chequea ANTES que "nba".
function gameDurationFromLeague(liga) {
  const ln = String(liga || '').toLowerCase();
  if (ln.includes('wnba') || ln.includes('nba w') || /\bnba\s*w(omen)?\b/.test(ln)) return 40;
  if (ln.includes('nba') || ln.includes('g league') || ln.includes('g-league')) return 48;
  return 40; // FIBA standard (10-min)
}

function EdgeChart({ pick }) {
  const hist = (Array.isArray(pick?.edge_history) ? pick.edge_history : [])
    .filter((p) => p && typeof p.e === 'number' && typeof p.t === 'number');
  if (hist.length < 2) return null;

  // Modo handball (2026-09-04): mismo gráfico, eje temporal 0-60 con marca
  // de HT, umbral EGH1 ±3 (vs ±15 de básquet) y franja sombreada de la
  // ventana operativa EGH1 (2H min 39-47).
  const isHandball = pick?.match_type === 'handball' || pick?.sport === 'handball';
  // Prioriza pick.duracion si el bot la manda; si no, la deriva de la liga.
  const dur = isHandball ? 60 : (Number(pick?.duracion) || gameDurationFromLeague(pick?.liga));
  const qLen = dur / 4;
  const UMBRAL = isHandball ? 3 : 15;
  const maxAbs = Math.max(isHandball ? 5 : 15, ...hist.map((p) => Math.abs(p.e)));
  const DMAX = Math.ceil(maxAbs / 5) * 5;

  const last = hist[hist.length - 1];
  const peak = hist.reduce((m, p) => (p.e > m.e ? p : m), hist[0]);
  const trough = hist.reduce((m, p) => (p.e < m.e ? p : m), hist[0]);
  const hit15 = peak.e >= UMBRAL;
  const hitN15 = trough.e <= -UMBRAL;

  // Split-gradient: offset donde y=0 cae dentro del bbox del área (verde
  // arriba del 0 = OVER, azul abajo = UNDER).
  const dMax = Math.max(...hist.map((p) => p.e));
  const dMin = Math.min(...hist.map((p) => p.e));
  let off;
  if (dMax <= 0) off = 0;
  else if (dMin >= 0) off = 1;
  else off = dMax / (dMax - dMin);

  // Línea O/U (puntos) en vivo. Solo se dibuja si la serie trae `l`. Eje propio.
  const lVals = hist.map((p) => p.l).filter((v) => typeof v === 'number');
  const hasLine = lVals.length >= 2;
  const lMin = hasLine ? Math.min(...lVals) : 0;
  const lMax = hasLine ? Math.max(...lVals) : 0;
  const lPad = Math.max(1, (lMax - lMin) * 0.3);
  const lDomain = [Math.floor(lMin - lPad), Math.ceil(lMax + lPad)];

  const qLabel = (v) => {
    if (isHandball) {
      if (v === 0) return '1T';
      if (Math.abs(v - 30) < 0.5) return 'HT';
      if (Math.abs(v - 45) < 0.5) return '2T';
      if (Math.abs(v - 60) < 0.5) return 'FT';
      return '';
    }
    if (v === 0) return 'Q1';
    if (Math.abs(v - qLen) < 0.5) return 'Q2';
    if (Math.abs(v - qLen * 2) < 0.5) return 'Q3';
    if (Math.abs(v - qLen * 3) < 0.5) return 'Q4';
    if (Math.abs(v - dur) < 0.5) return 'FT';
    return '';
  };

  return (
    <section className="md-section ec-section">
      <div className="ec-head">
        <div className="ec-titles">
          <h3 style={{ margin: 0 }}>Edge + línea de la casa de apuestas</h3>
          <div className="ec-sub">Timing en tiempo real para decidir tu entrada</div>
        </div>
        <div className="ec-badges">
          {hit15 && <span className="ec-badge over">tocó +{Math.round(peak.e)}</span>}
          {hitN15 && <span className="ec-badge under">tocó {Math.round(trough.e)}</span>}
        </div>
      </div>

      <div className="ec-chart">
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={hist} margin={{ top: 16, right: 4, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="ec-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset={0} stopColor="#22c55e" stopOpacity={0.5} />
                <stop offset={off} stopColor="#22c55e" stopOpacity={0.12} />
                <stop offset={off} stopColor="#3b82f6" stopOpacity={0.12} />
                <stop offset={1} stopColor="#3b82f6" stopOpacity={0.5} />
              </linearGradient>
              <linearGradient id="ec-stroke" x1="0" y1="0" x2="0" y2="1">
                <stop offset={0} stopColor="var(--pos)" />
                <stop offset={off} stopColor="var(--pos)" />
                <stop offset={off} stopColor="var(--info)" />
                <stop offset={1} stopColor="var(--info)" />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="t" type="number" domain={[0, dur]}
              ticks={[0, qLen, qLen * 2, qLen * 3, dur]} tickFormatter={qLabel}
              tick={{ fontSize: 10, fill: 'var(--muted)' }}
              tickLine={false} axisLine={{ stroke: 'var(--line)' }}
            />
            {/* Eje izq = línea O/U puntos (si hay serie); eje der = edge */}
            {hasLine && (
              <YAxis
                yAxisId="linea" orientation="left" domain={lDomain} width={34}
                tick={{ fontSize: 10, fill: 'var(--warn)' }} tickFormatter={(v) => Math.round(v)}
                tickLine={false} axisLine={false} allowDecimals={false}
              />
            )}
            <YAxis
              yAxisId="edge" orientation="right" domain={[-DMAX, DMAX]} ticks={[DMAX, 0, -DMAX]} width={30}
              tick={{ fontSize: 10, fill: 'var(--muted)' }}
              tickLine={false} axisLine={false}
            />

            {/* Franja EGH1 (handball): ventana operativa 2H min 39-47 */}
            {isHandball && (
              <ReferenceArea
                yAxisId="edge" x1={39} x2={47}
                fill="var(--brand, #FF5C1A)" fillOpacity={0.08}
                stroke="var(--brand, #FF5C1A)" strokeOpacity={0.35} strokeDasharray="4 3"
                label={{ value: 'EGH1', position: 'insideTopRight', fill: 'var(--brand, #FF5C1A)', fontSize: 10, fontWeight: 800 }}
              />
            )}
            {(isHandball ? [30] : [qLen, qLen * 2, qLen * 3]).map((q) => (
              <ReferenceLine key={q} yAxisId="edge" x={q} stroke="var(--line)" strokeWidth={1} />
            ))}
            <ReferenceLine yAxisId="edge" y={UMBRAL} stroke="var(--pos)" strokeDasharray="3 3" strokeOpacity={0.6} />
            <ReferenceLine yAxisId="edge" y={-UMBRAL} stroke="var(--info)" strokeDasharray="3 3" strokeOpacity={0.6} />
            <ReferenceLine yAxisId="edge" y={0} stroke="var(--muted)" strokeOpacity={0.5} />

            <Area
              yAxisId="edge" type="monotone" dataKey="e" baseValue={0}
              stroke="url(#ec-stroke)" strokeWidth={1.8}
              fill="url(#ec-fill)" dot={false} isAnimationActive={false}
            />

            {hasLine && (
              <Line
                yAxisId="linea" type="monotone" dataKey="l"
                stroke="var(--warn)" strokeWidth={1.8} dot={false}
                isAnimationActive={false} connectNulls
              />
            )}

            <ReferenceLine
              yAxisId="edge" x={last.t} stroke="var(--neg)" strokeWidth={1.2}
              label={{ value: `${last.e >= 0 ? '+' : ''}${last.e.toFixed(1)}`, position: 'top', fill: 'var(--neg)', fontSize: 10, fontWeight: 800 }}
            />

            <Tooltip content={<EdgeTooltip qLen={qLen} handball={isHandball} />} cursor={{ stroke: 'var(--muted)', strokeDasharray: '3 3' }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="ec-legend">
        <span><i className="over" /> OVER</span>
        <span><i className="under" /> UNDER</span>
        {hasLine && <span><i className="linea" /> Línea O/U</span>}
        <span className="ec-legend-q">⋯ umbral ±{UMBRAL}{isHandball ? ' · franja = ventana EGH1' : ''}</span>
      </div>

      <style>{`
        .ec-section { padding-bottom: 12px; }
        .ec-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
        .ec-sub { font-size: 11px; font-weight: 500; color: var(--muted); margin-top: 2px; }
        .ec-badges { display: flex; gap: 6px; flex-shrink: 0; }
        .ec-badge { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 999px; letter-spacing: .02em; }
        .ec-badge.over { color: var(--pos); background: rgba(34,197,94,.14); }
        .ec-badge.under { color: var(--info); background: rgba(59,130,246,.14); }
        .ec-chart { margin-top: 6px; }
        .ec-legend { display: flex; align-items: center; gap: 12px; margin-top: 4px;
          font-size: 10.5px; font-weight: 600; color: var(--muted); }
        .ec-legend span { display: inline-flex; align-items: center; gap: 5px; }
        .ec-legend i { width: 9px; height: 9px; border-radius: 2px; display: inline-block; }
        .ec-legend i.over { background: var(--pos); }
        .ec-legend i.under { background: var(--info); }
        .ec-legend i.linea { background: var(--warn); }
        .ec-legend-q { margin-left: auto; font-weight: 500; }
      `}</style>
    </section>
  );
}

// Etiqueta de tiempo para handball: mitad + minuto corrido (nunca Q1/Q2).
function periodLabelHb(pick) {
  const mitad = Number(pick.period) === 2 ? '2T' : '1T';
  const min = pick.minuto != null ? Math.round(pick.minuto) : null;
  return min != null ? `${mitad} · min ${min}` : mitad;
}

// ── Contexto HANDBALL (2026-09-04) — reemplaza las 3 columnas de básquet
// en partidos match_type=handball: marcador por mitad, diferencia (el
// filtro EGH1), línea/proyección/edge. El gráfico EdgeChart va arriba
// (mismo componente, modo handball con franja EGH1 2H39-47).
function HandballContext({ pick }) {
  const m = pick.mitades || {};
  const diff = pick.diff != null ? pick.diff : (() => {
    const [a, h] = String(pick.score || '').split('-').map((x) => parseInt(x, 10));
    return Number.isNaN(a) || Number.isNaN(h) ? null : Math.abs(a - h);
  })();
  const edge = parseFloat(pick.edge);
  const enVentana = pick.minuto >= 39 && pick.minuto <= 47;
  return (
    <div className="md-body" style={{ gridTemplateColumns: '1fr 1fr' }}>
      <div className="md-col">
        <section className="md-section">
          <h3>Marcador por mitad</h3>
          <div className="kv">
            <KvRow label="1ª mitad"><span className="kv-v">{m.h1 || '—'}</span></KvRow>
            <KvRow label="2ª mitad"><span className="kv-v">{m.h2 || '—'}</span></KvRow>
            <KvRow label="Total"><span className="kv-v">{pick.score || '—'}</span></KvRow>
            <KvRow label="Diferencia de goles">
              <span className="kv-v" style={diff != null && diff >= 4 ? { color: 'var(--pos)' } : {}}>
                {diff != null ? diff : '—'}{diff != null && diff >= 4 ? ' ✓ EGH1' : ''}
              </span>
            </KvRow>
            <KvRow label="Minuto"><span className="kv-v">{pick.minuto != null ? `${Math.round(pick.minuto)}'` : '—'}{enVentana ? ' · en ventana EGH1' : ''}</span></KvRow>
          </div>
        </section>
      </div>
      <div className="md-col">
        <section className="md-section">
          <h3>Modelo</h3>
          <div className="kv">
            <KvRow label="Línea O/U live"><span className="kv-v">{pick.linea ?? '—'}</span></KvRow>
            <KvRow label="Línea prematch"><span className="kv-v">{pick.linea_pre ?? '—'}</span></KvRow>
            <KvRow label="Proyección"><span className="kv-v">{pick.projection ?? '—'}<span className="u"> goles</span></span></KvRow>
            <KvRow label="Edge">
              <span className="kv-v" style={!Number.isNaN(edge) && edge >= 3 ? { color: 'var(--pos)' } : {}}>
                {Number.isNaN(edge) ? '—' : `${edge >= 0 ? '+' : ''}${edge.toFixed(1)}`}
              </span>
            </KvRow>
            <KvRow label="Ritmo del partido">
              <span className="kv-v">
                {pick.ritmo_obs ?? '—'}
                <span className="u"> vs {pick.ritmo_esp ?? '—'} esperado</span>
              </span>
            </KvRow>
            <KvRow label="Media de la liga"><span className="kv-v">{pick.liga_media ?? '—'}<span className="u"> goles</span></span></KvRow>
          </div>
          <Callout ok={!Number.isNaN(edge) && edge >= 3 && enVentana}>
            {!Number.isNaN(edge) && edge >= 3 && enVentana
              ? 'Condiciones de señal completas: 2ª mitad y edge ≥ +3.'
              : 'La señal requiere: 2ª mitad (min 31-55) y edge ≥ +3 goles.'}
          </Callout>
        </section>

        {(pick.perfil_home || pick.perfil_away) && (
          <section className="md-section">
            <h3>Perfil de los equipos</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 60px', gap: 6, fontSize: 12 }}>
              <span style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Promedio por partido</span>
              <span style={{ fontSize: 10, color: 'var(--muted)', textAlign: 'right' }}>{pick.away_team || 'Visita'}</span>
              <span style={{ fontSize: 10, color: 'var(--muted)', textAlign: 'right' }}>{pick.home_team || 'Local'}</span>
              {[['Anota 1ª mitad', 'off_1h'], ['Recibe 1ª mitad', 'def_1h'],
                ['Anota 2ª mitad', 'off_2h'], ['Recibe 2ª mitad', 'def_2h'],
                ['Anota total', 'off_total'], ['Recibe total', 'def_total']].map(([lbl, k]) => (
                <React.Fragment key={k}>
                  <span style={{ color: 'var(--muted)' }}>{lbl}</span>
                  <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {pick.perfil_away ? pick.perfil_away[k] : '—'}
                  </span>
                  <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {pick.perfil_home ? pick.perfil_home[k] : '—'}
                  </span>
                </React.Fragment>
              ))}
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 8 }}>
              Muestra: últimos {(pick.perfil_home && pick.perfil_home.n) || (pick.perfil_away && pick.perfil_away.n) || '—'} partidos de cada equipo.
              Sin perfil suficiente, el modelo usa la media de la liga.
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default function DetailPanel({ pick, onClose, inline, lineOverride }) {
  if (!pick) return null;
  const isHandball = pick.match_type === 'handball' || pick.sport === 'handball';

  const manualLine = lineOverride?.line;
  const displayLine = manualLine || pick.linea || pick.line || pick.over_under;

  // FIX: siempre usar pick.edge de Firebase (ya viene ajustado por confidence desde el engine).
  const displayEdge = parseFloat(pick.edge || 0);

  // Veredicto — también basado en el edge real de Firebase
  let vc, vl;
  {
    const fbEdge = parseFloat(pick.edge || 0);
    const hasLine = displayLine && parseFloat(displayLine) > 0;
    const canBeFuerte = (pick.timing||'').toUpperCase()==='GO' && parseStake(pick.stake)>0;

    if (hasLine && fbEdge !== 0) {
      if (fbEdge<=-8&&canBeFuerte)      {vc='fuerte-under';vl='UNDER ★';}
      else if (fbEdge<=-6)              {vc='under';       vl='VIGILAR';}
      else if (fbEdge>=8&&canBeFuerte)  {vc='fuerte-over'; vl='OVER ★';}
      else if (fbEdge>=6)               {vc='over';        vl='VIGILAR';}
      else                              {vc='wait';        vl='ESPERAR';}
    } else {
      vc = getVerdictClass(pick.decision);
      vl = getVerdictLabel(pick.decision);
    }
  }

  const trampas   = pick.trampas;
  const hasTrampas = typeof trampas==='number' && trampas>0;
  const noStake   = parseStake(pick.stake)===0;
  if (hasTrampas && noStake) { vc='wait'; vl='ESPERAR'; }

  const edge = displayEdge;
  const isFuerte  = vc==='fuerte-under'||vc==='fuerte-over';
  const isVigilar = vc==='under'||vc==='over';

  const vcTone = isFuerte?'enter': isVigilar?'watch': vc==='wait'?'wait':'pass';

  const edgeClass  = edge<0?'neg': edge>0?'pos':'';
  const timingColor = pick.timing==='GO'?'var(--v-enter)': pick.timing==='CANCELAR'?'var(--neg)':'var(--warn)';

  const qtrDetail = Array.isArray(pick.qtr_detail) ? pick.qtr_detail : [];

  const subText = (hasTrampas&&noStake)
    ? (pick.razon||`${trampas} trampa${trampas===1?'':'s'} activa${trampas===1?'':'s'} — no apostar`)
    : pick.razon&&!pick.razon.toLowerCase().includes('sin línea') ? pick.razon : '';

  function paceTag(adj) {
    const a = (adj||'').toLowerCase();
    if (a.includes('muy alto')) return <Tag tone="warn">MUY ALTO</Tag>;
    if (a.includes('alto'))     return <Tag tone="warn">ALTO</Tag>;
    if (a.includes('bajo'))     return <Tag tone="low">BAJO</Tag>;
    if (a.includes('normal'))   return <Tag tone="info">NORMAL</Tag>;
    return <span className="kv-v">{fmt(adj)}</span>;
  }
  function impactoTag(imp) {
    const i = (imp||'').toLowerCase();
    if (i.includes('bajo'))  return <Tag tone="low">BAJO</Tag>;
    if (i.includes('medio')) return <Tag tone="info">MEDIO</Tag>;
    if (i.includes('alto'))  return <Tag tone="warn">ALTO</Tag>;
    return <span className="kv-v">{fmt(imp)}</span>;
  }
  function tipoTag(tipo) {
    if (!tipo||tipo==='-'||tipo==='—') return <span className="kv-v muted">—</span>;
    return <Tag tone="info">{tipo}</Tag>;
  }
  function momentoTag(timing) {
    if (!timing||timing==='-') return <span className="kv-v muted">—</span>;
    if (timing==='GO')      return <Tag tone="go">GO</Tag>;
    if (timing==='CANCELAR') return <span className="kv-v neg">{timing}</span>;
    return <span className="kv-v" style={{color:'var(--warn)'}}>{timing}</span>;
  }

  const [awayScore, homeScore] = (() => {
    const s = pick.score||'';
    const parts = s.split('-').map(x=>parseInt(x.trim(),10));
    return [parts[0]||0,parts[1]||0];
  })();
  const total = awayScore + homeScore;

  // Q1+Q2 → calibrando (sin proyección visible). Q3 → preliminar (proyección
  // + disclaimer). Q4+ → normal sin disclaimers. Cambio puramente UX
  // 2026-05-04 — el motor sigue trabajando igual, esto solo afecta lo que ve
  // el cliente en el modal de detalle del partido. Telegram alerts no cambian.
  const periodNum     = Number(pick?.period);
  const isCalibrating = periodNum >= 1 && periodNum <= 2;
  const isPreliminary = periodNum === 3;

  return (
    <div
      className="detail-panel"
      style={inline ? { marginTop:0, borderRadius:0, border:'none', borderTop:'1px solid var(--line)' } : {}}
    >
      {/* ── BREADCRUMB / HEADER ── */}
      <div className="md-breadcrumb">
        <div>
          <b>{pick.partido || '—'}</b>
          <span className="sep">·</span>
          <span className="q">{isHandball ? periodLabelHb(pick) : `Q${pick.period||'?'} ${pick.clock||''}`}</span>
          <span className="sep">·</span>
          <span>{pick.liga||''}</span>
        </div>
        <a className="close-x" onClick={onClose}>Cerrar ×</a>
      </div>

      {/* ── SUMMARY ROW (rediseño moderno; sin verdict OU ni "API·vivo") ── */}
      <div className="msx">
        <div className="msx-head">
          <span className="msx-live"><span className="msx-dot" />EN VIVO</span>
          <div className="msx-teamrow">
            {pick.away_logo && (
              <img src={pick.away_logo} alt="" className="msx-logo"
                onError={e => e.currentTarget.style.display='none'} />
            )}
            <span className="msx-teams">{pick.partido || '—'}</span>
            {pick.home_logo && (
              <img src={pick.home_logo} alt="" className="msx-logo"
                onError={e => e.currentTarget.style.display='none'} />
            )}
          </div>
          <span className="msx-meta">{isHandball ? periodLabelHb(pick) : `Q${pick.period||'?'} ${pick.clock||''}`} · {pick.liga||''}</span>
        </div>

        <div className="msx-stats">
          <div className="msx-card">
            <div className="msx-k">Marcador</div>
            <div className="msx-v">
              {pick.score
                ? (<><span className="msx-abbr">{pick.away_team || '?'}</span> {awayScore} <span className="msx-sep">–</span> {homeScore} <span className="msx-abbr">{pick.home_team || '?'}</span></>)
                : '—'}
            </div>
            <div className="msx-sub"><span className="msx-livemini"><span className="msx-mini-dot" />{total>0?`${total} pts`:'en vivo'}</span></div>
          </div>

          <div className="msx-card">
            <div className="msx-k">Línea de tu casa de apuestas</div>
            <div className="msx-v">{displayLine||'—'}</div>
            <div className="msx-sub"><span className="msx-livemini"><span className="msx-mini-dot" />en vivo</span></div>
          </div>

          <div className="msx-card">
            <div className="msx-k">Proyección</div>
            <div className="msx-v">{isCalibrating ? '—' : (pick.proyeccion ? `${pick.proyeccion}` : '—')}</div>
            <div className="msx-sub">{isCalibrating ? ' ' : 'pts'}</div>
          </div>

          <div className="msx-card">
            <div className="msx-k">Ventaja</div>
            {isCalibrating ? (
              <div className="msx-v msx-analy"><span className="msx-analy-dot" />Analizando</div>
            ) : (
              <div className={`msx-v ${edgeClass}`}>{edge!==0?`${edge>=0?'+':''}${edge.toFixed(1)}`:'—'}</div>
            )}
            <div className="msx-sub">{' '}</div>
          </div>

          <div className="msx-card">
            <div className="msx-k">Confianza</div>
            <div className="msx-v">{pick.confianza?`${parseFloat(pick.confianza).toFixed(1)}`:'—'}</div>
            <div className="msx-sub">{' '}</div>
          </div>
        </div>

        <style>{`
          .msx { padding: 12px 16px 12px; border-bottom: 1px solid var(--line); }
          .msx-head { display: flex; flex-direction: column; align-items: center; gap: 5px; text-align: center; }
          .msx-live { display: inline-flex; align-items: center; gap: 5px; font-size: 9px;
            font-weight: 700; letter-spacing: .1em; color: var(--pos, #16a34a);
            background: color-mix(in oklab, var(--pos, #16a34a) 12%, transparent);
            padding: 2px 8px; border-radius: 999px; }
          .msx-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--pos, #16a34a);
            animation: msx-pulse 1.6s infinite; }
          @keyframes msx-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
          .msx-teamrow { display: flex; align-items: center; justify-content: center; gap: 8px; }
          .msx-logo { width: 22px; height: 22px; object-fit: contain; }
          .msx-teams { font-size: 14px; font-weight: 700; letter-spacing: -0.01em; color: var(--ink); }
          .msx-meta { font-size: 11px; color: var(--muted); font-weight: 500; }

          .msx-stats { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px;
            margin-top: 12px; align-items: start; }
          .msx-card { display: flex; flex-direction: column; align-items: center; gap: 2px;
            text-align: center; }
          .msx-k { font-size: 9px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
            color: var(--muted); line-height: 1.25;
            min-height: 36px; display: flex; align-items: center; justify-content: center; }
          .msx-v { font-size: 15px; font-weight: 700; color: var(--ink); letter-spacing: -0.01em;
            font-variant-numeric: tabular-nums; line-height: 1.15; white-space: nowrap; }
          .msx-v.neg { color: var(--neg, #dc2626); }
          .msx-v.pos { color: var(--pos, #16a34a); }
          .msx-abbr { font-size: 9.5px; font-weight: 700; color: var(--muted); letter-spacing: .02em; }
          .msx-sep { color: var(--muted-2, #9aa0a6); margin: 0 1px; }
          .msx-sub { font-size: 11.5px; color: var(--ink-2, #3a3a40); font-weight: 600; min-height: 15px;
            font-variant-numeric: tabular-nums; }
          .msx-livemini { display: inline-flex; align-items: center; gap: 4px; }
          .msx-mini-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--neg, #dc2626);
            animation: msx-pulse 1.4s infinite; flex-shrink: 0; }
          .msx-analy { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px;
            font-weight: 600; color: var(--warn, #d97706); }
          .msx-analy-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--warn, #d97706);
            animation: msx-pulse 1.4s infinite; }

          @media (max-width: 520px) {
            .msx-stats { grid-template-columns: repeat(2, 1fr); }
          }
        `}</style>
      </div>

      {/* Gráfico Edge full-width (timing) — sobre las 3 columnas, más ancho en desktop */}
      <div className="md-chart-full">
        <EdgeChart pick={pick} />
      </div>

      {/* ── 3 COLUMNS (básquet) / contexto handball ── */}
      {isHandball ? <HandballContext pick={pick} /> : (
      <div className="md-body">

        {/* COL 1 — Cuartos + Modelo + Team Pace */}
        <div className="md-col">
          <section className="md-section">
            <h3>Cuartos en detalle</h3>
            <div className="qt-table">
              <div className="qt-head">
                <span>Q</span><span>PTS</span><span>MIN</span><span>RITMO/48</span><span>RELOJ</span>
              </div>
              {qtrDetail.length === 0 ? (
                <div className="qt-row" style={{ color: 'var(--muted)', justifyContent: 'center' }}>Sin datos</div>
              ) : qtrDetail.map((q, i) => (
                <div key={i} className={`qt-row ${q.live ? 'live' : ''}`}>
                  <span className="qt-q">Q{q.q}</span>
                  <span className="qt-n">{q.pts}</span>
                  <span className="qt-n muted">{q.min}m</span>
                  <span className="qt-n">{q.pace}</span>
                  <span className={`qt-n ${q.live ? 'accent' : 'muted'}`}>{q.live ? `${pick.clock||''} ◀` : 'FIN'}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="md-section">
            <h3>Modelo cuantitativo</h3>
            <div className="kv">
              <KvRow label="Ritmo partido"><span className="kv-v">{fmt(pick.pace)}<span className="u"> pts/48</span></span></KvRow>
              <KvRow label={`Ritmo liga (${pick.liga||''})`}><span className="kv-v">{fmt(pick.liga_pace)}<span className="u"> pts/48</span></span></KvRow>
              <KvRow label="Ritmo ajustado">{paceTag(pick.pace_adj)}</KvRow>
              <KvRow label="Eficiencia ofensiva"><span className="kv-v">{fmt(pick.ef_ofensiva)}</span></KvRow>
              <KvRow label="Muestra (min)"><span className="kv-v">{fmt(pick.min_jugados)}<span className="u"> / {pick.duracion||48}</span></span></KvRow>
            </div>

            {/* ═══ NUEVO: Análisis de pace por equipo ═══ */}
            <TeamPaceAnalysis pick={pick} />
          </section>
        </div>

        {/* COL 2 — Momentum + Stats */}
        <div className="md-col">
          <section className="md-section">
            <h3>Momentum & Hándicap</h3>
            <div className="kv">
              <KvRow label="Hándicap"><span className="kv-v">{fmt(pick.handicap)}</span></KvRow>
              <KvRow label="Tipo partido">{tipoTag(pick.tipo_partido)}</KvRow>
              <KvRow label="Racha"><span className="kv-v">{fmt(pick.racha)}</span></KvRow>
              <KvRow label="Ajuste modelo"><span className="kv-v">{pick.ajuste_modelo&&pick.ajuste_modelo!==0?pick.ajuste_modelo:<span className="kv-v muted">—</span>}</span></KvRow>
              <KvRow label="Impacto">{impactoTag(pick.impacto)}</KvRow>
            </div>
          </section>

          <section className="md-section md-stats-team">
            <div className="md-section-head">
              <span className="md-section-title">Stats clave</span>
              <span className="md-section-meta">
                Q{pick?.period || '—'} · {pick?.min_jugados || '—'} min
              </span>
            </div>

            <StatsComparison pick={pick} />
          </section>

          {/* Rangos de referencia por liga (Fase 2 — thresholds empíricos Q3+) */}
          <section className="md-section">
            <LeagueRangesTable
              liga={pick?.liga || ''}
              values={{
                pace: pick?.pace,
                ef: pick?.ef_ofensiva,
                fg_pct: pick?.fg_pct_real ?? pick?.fg_pct,
                fg3_pct: pick?.fg3_pct_real ?? pick?.fg3_pct,
                turnovers: pick?.turnovers_real ?? pick?.turnovers,
                fta: pick?.fta_real ?? pick?.fta,
              }}
            />
          </section>
        </div>

        {/* COL 3 — Veredicto Pro */}
        <div className="md-col">
          <section className="md-section">
            <h3>Veredicto Pro</h3>
            {isCalibrating ? (
              <div className="calibrating-card">
                <div className="calibrating-spinner" />
                <div className="calibrating-title">Analizando partido</div>
                <div className="calibrating-body">
                  Edge Elite necesita al menos un cuarto completo para calibrar el modelo.
                  Estamos midiendo ritmo, eficiencia ofensiva y momentum. El veredicto se activará al inicio del Q3.
                </div>
              </div>
            ) : (
              <>
                <div className="kv">
                  <KvRow label="Proyección"><span className="kv-v">{fmt(pick.proyeccion)}<span className="u"> pts</span></span></KvRow>
                  <KvRow label="+ Momentum"><span className="kv-v">{fmt(pick.proy_momentum)}<span className="u"> pts</span></span></KvRow>
                  <KvRow label="Línea O/U"><span className="kv-v">{displayLine||'—'}</span></KvRow>
                  <KvRow label="Ventaja base"><span className={`kv-v ${edgeClass}`}>{edge!==0?`${edge>=0?'+':''}${edge.toFixed(1)}`:'—'}</span></KvRow>
                  <KvRow label="Tipo juego">{tipoTag(pick.script&&pick.script!=='-'?pick.script:pick.tipo_partido)}</KvRow>
                  <KvRow label="Momento">{momentoTag(pick.timing)}</KvRow>
                </div>

                {/* Callout del timing */}
                {pick.timing === 'GO' ? (
                  <Callout ok>Ritmo bajo confirmado y sostenido — condiciones OK para entrar</Callout>
                ) : pick.timing === 'CANCELAR' ? (
                  <Callout ok={false}>Ritmo subió de nuevo — señal cancelada, no entrar</Callout>
                ) : pick.timing_razon ? (
                  <Callout ok={false}>{pick.timing_razon}</Callout>
                ) : (
                  <Callout ok={false}>Sin transición de ritmo detectada</Callout>
                )}

                <div className="kv" style={{ marginTop: 12 }}>
                  <KvRow label="Trampas">
                    <span className="kv-v" style={{ color: !trampas ? 'var(--v-enter)' : 'var(--warn)' }}>
                      {trampas !== undefined ? trampas : '—'}
                    </span>
                  </KvRow>
                  <KvRow label="Apuesta sugerida">
                    <span className="kv-v">{fmt(pick.stake)}</span>
                  </KvRow>
                </div>

              </>
            )}
          </section>
        </div>
      </div>
      )}
    </div>
  );
}
